'use client';

import { useParams, useRouter } from 'next/navigation';
import { FC, useEffect, useRef } from 'react';

import { Pill } from '@/components/InterestsList/Pill';

import styles from './CategoryList.module.scss';
import { ICategoryList } from './CategoryList.types';

/**
 * Renders a list of category pills that can be scrolled horizontally and selected.
 * Each pill represents a category, and selecting a pill updates the route.
 * @component
 * @param {ICategoryList} props - Props for the CategoryList component.
 * @returns {JSX.Element} A list of selectable categories.
 */
export const CategoryList: FC<ICategoryList> = ({ categories }) => {
  const { id } = useParams();
  const route = useRouter();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const pill = useRef<HTMLLabelElement | null>(null);
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isDragModeRef = useRef(false);

  /**
   * Handles the click event on a category, updating the route with the selected category ID.
   * @param {string} id - The ID of the selected category.
   */
  const handleClickCategory = (id: string) => {
    route.replace(id);
  };

  useEffect(() => {
    if (!pill.current || !id) return;

    // Finds the selected category and scrolls it into view if it exists.
    const categorySelected = categories.find(({ value }) => value === id);
    if (!categorySelected) return;

    const escapedLabel = CSS.escape(categorySelected.label);

    const element = document.querySelector(
      `label[for="${escapedLabel}"]`,
    ) as HTMLElement | null;

    element?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, [pill, id, categories]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let isDown = false;
    let startX: number;
    let scrollLeft: number;

    /**
     * Initiates drag mode if the mouse is held down for a certain duration.
     * @param {MouseEvent} e - The mouse event.
     */
    const handleMouseDown = (e: MouseEvent) => {
      isDown = true;
      isDragModeRef.current = false;
      startX = e.pageX - container.offsetLeft;
      scrollLeft = container.scrollLeft;

      clickTimeoutRef.current = setTimeout(() => {
        isDragModeRef.current = true;
        container.classList.add('active');
      }, 200);
    };

    /**
     * Ends drag mode on mouse release, triggering category click if drag mode is not active.
     */
    const handleMouseUp = (e: MouseEvent) => {
      isDown = false;
      container.classList.remove('active');

      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
        clickTimeoutRef.current = null;
      }

      if (!isDragModeRef.current) {
        const target = e.target as HTMLElement;
        const categoryId = target.getAttribute('data-value');
        if (categoryId) {
          handleClickCategory(categoryId);
        }
      }
    };

    /**
     * Handles mouse movement, enabling horizontal scrolling if the mouse is down.
     * @param {MouseEvent} e - The mouse event.
     */
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDown || !isDragModeRef.current) return;
      e.preventDefault();
      const x = e.pageX - container.getBoundingClientRect().left;

      // Adjusts scroll speed
      const walk = (x - startX) * 1;
      container.scrollLeft = scrollLeft - walk;
    };

    /**
     * Handles the wheel scroll event for horizontal scrolling.
     * @param {WheelEvent} e - The wheel event.
     */
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      container.scrollLeft += e.deltaY;
    };

    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('wheel', handleWheel);
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
    };
  }, [categories, handleClickCategory]);

  return (
    <div ref={containerRef} className={styles.categoriesPillContainer}>
      {categories.map(({ value, label }) => (
        <Pill
          ref={pill}
          key={value}
          name={label}
          value={value}
          data-value={value}
          checked={value === id}
          onChange={() => {
            if (!isDragModeRef.current) {
              handleClickCategory(value);
            }
          }}
        />
      ))}
    </div>
  );
};
